import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { SatelliteMachineViewStore } from "../../store";
import { SatelliteMachineView } from "../view/SatelliteMachineView";
import { SatelliteMachineUpdateStore } from "../../store";
import { SatelliteMachineUpdate } from "../update/SatelliteMachineUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type SatelliteMachineModalContentProps = {
  entityId: string;
  mode: "view" | "update";
  onClose: () => void;
};

export const SatelliteMachineModalContent: React.FC<SatelliteMachineModalContentProps> =
  observer(({ entityId, mode, onClose }) => {
    const rootStore = useRootStore();
    const initializedRef = useRef<string | null>(null);

    const storeInfo = useMemo(() => {
      if (mode === "update") {
        const s = new SatelliteMachineUpdateStore(rootStore);
        s.isModalMode = true;
        return { store: s, type: "update" as const };
      }
      // Default to view mode
      const s = new SatelliteMachineViewStore(rootStore);
      s.isModalMode = true;
      return { store: s, type: "view" as const };
    }, [rootStore, mode]);

    useEffect(() => {
      if (entityId && initializedRef.current !== entityId) {
        initializedRef.current = entityId;
        storeInfo.store.initialize({ id: entityId });
      }
    }, [entityId, storeInfo]);

    useEffect(() => {
      return () => {
        storeInfo.store.destroy();
      };
    }, [storeInfo]);

    const store = storeInfo.store;
    if (store.status === "processing" || store.status === "uninitialized") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {storeInfo.type === "view" && (
          <SatelliteMachineView
            satelliteMachineViewStore={
              storeInfo.store as SatelliteMachineViewStore
            }
          />
        )}
        {storeInfo.type === "update" && (
          <SatelliteMachineUpdate
            satelliteMachineUpdateStore={
              storeInfo.store as SatelliteMachineUpdateStore
            }
            isModalMode
            onModalClose={onClose}
          />
        )}
      </Box>
    );
  });
